import { ScrollView, TextInput, View } from 'react-native';
import { ToolOptionsRendererProps } from '../../../BaseToolbar.types';
import { ToolOptionsProps } from '../ToolOptionsRenderer.types';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { styles } from './TextToolOptions.styles';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import DropdownCustom from '@photosi/rn-components/dropdown-custom/DropdownCustom';
import { ALIGNMENT, CustomFontName } from 'src/constants/font.constants';
import Alignment from '@photosi/rn-components/alignment/Alignment';
import { PrimaryButton } from '@photosi/rn-components/buttons/primary-button/PrimaryButton';
import { ColorToolOption } from '../color-tool/color-tool-option/ColorToolOption';
import { useTextToolOptionsLogic } from './TextToolOptions.logic';
import { getAvailableCustomFontStyles } from 'src/helpers/font.helper';
import { configurationTestID, lookupChoiceTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';

export const TextToolOptions = observer((props: ToolOptionsRendererProps<ToolOptionsProps>) => {
  const { i18n } = useStore();
  const {
    scrollViewRef,
    getScrollViewElementTestId,
    handleConfirmButton,
    text,
    font,
    style,
    alignment,
    color,
    colorPaletteOptions,
    fontSize,
    fontSizes,
    onChangeText,
    onChangeFont,
    onChangeStyle,
    onChangeAlignment,
    onChangeColor,
    cleanValueText,
    onChangeFontSize,
  } = useTextToolOptionsLogic(props);

  return (
    <>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.contentContainer]}
        ref={scrollViewRef}
        showsVerticalScrollIndicator
        persistentScrollbar
      >
        <View>
          <TextLato style={styles.text}>{i18n.t('text-tool-option:title')}</TextLato>
          <View style={styles.input}>
            <TextInput
              testID={lookupChoiceTestID(TestIDs.TEXT, 0, TestIDs.TEXT)}
              style={styles.inputText}
              onChangeText={onChangeText}
              value={text}
              maxLength={20}
              onBlur={cleanValueText}
              multiline
            />
            <TextLato style={styles.counter}>{text.length}/20</TextLato>
          </View>
        </View>

        <DropdownCustom
          testID={lookupChoiceTestID(TestIDs.TEXT, 1, TestIDs.TEXT_FONT)}
          label={i18n.t('text-tool-option:font')}
          options={Object.values(CustomFontName)}
          selected={font}
          onSelect={onChangeFont}
          isFont
        />

        <DropdownCustom
          testID={lookupChoiceTestID(TestIDs.TEXT, 2, TestIDs.TEXT_STYLE)}
          label={i18n.t('text-tool-option:style')}
          options={getAvailableCustomFontStyles(font)}
          selected={style}
          onSelect={onChangeStyle}
        />

        <DropdownCustom
          testID={lookupChoiceTestID(TestIDs.TEXT, 3, TestIDs.TEXT_SIZE)}
          label={i18n.t('text-tool-option:size')}
          options={fontSizes}
          selected={fontSize}
          onSelect={onChangeFontSize}
        />

        <Alignment
          testID={lookupChoiceTestID(TestIDs.TEXT, 4, TestIDs.TEXT_ALIGNMENT)}
          label={i18n.t('text-tool-option:alignment')}
          options={ALIGNMENT}
          selected={alignment}
          onSelect={onChangeAlignment}
        />

        <View testID={lookupChoiceTestID(TestIDs.TEXT, 5, TestIDs.TEXT_COLOR)}>
          <TextLato style={styles.text}>{i18n.t('text-tool-option:color')}</TextLato>
          <View style={styles.containerToolOptions}>
            {colorPaletteOptions.map((option, index) => (
              <View
                key={`text-tool-options-${option?.choiceDetails?.color}`}
                testID={getScrollViewElementTestId(index)}
              >
                <ColorToolOption
                  index={index}
                  isActive={props.isActive}
                  isPriceVisible={false}
                  isSelected={option?.choiceDetails?.color === color}
                  isDisabled={!option.enabled}
                  optionCode={option?.productOptionCode}
                  testID={lookupChoiceTestID(TestIDs.TEXT, 5, TestIDs.TEXT_COLOR, option?.productOptionCode)}
                  color={option?.choiceDetails?.color}
                  onPress={() => onChangeColor(option.choiceDetails.color)}
                  title={option.titleKey && i18n.t(option.titleKey)}
                  hint={option?.hint && i18n.t(option?.hint)}
                />
              </View>
            ))}
          </View>
        </View>
      </ScrollView>

      <View style={styles.button}>
        <PrimaryButton
          testID={configurationTestID(TestIDs.TEXT, TestIDs.TEXT_SAVE_BUTTON)}
          onPress={handleConfirmButton}
          text={i18n.t('text-tool-option:button')}
        />
      </View>
    </>
  );
});
